import React, { useEffect, useState } from "react";
import { InputGroup, Form } from "react-bootstrap";
import { ClipLoader } from "react-spinners";
import SpecificAPI from "services/SpecificApi";
import { toast } from "react-toastify";
import localization from "services/localization";
import DeleteModal from "views/Components/DeleteModal";
import ManagementHeader from "views/Components/ManagementHeader";
import CustomSelect from "views/Components/CustomSelect";
import { PromptMangmentCard } from "views/Components/PromptMangmentCard";
import AddEditPromptCategoryModal from "./AddEditPromptCategoryModal";
import { usePromptCategoriesStore } from "context/usePromptCategoriesStore";
import { useModulesStore } from "context/useModulesStore";
import { ALL_TYPES_OPTIONS } from "services/constants";
import { FaSearch } from "react-icons/fa";
import { useLanguageStore } from "context/useLanguageStore";

const emptyPromptCat = {
  title: {},
  description: {},
  type: "teaching",
  moduleId: "",
  icon: "",
};
const PromptCatMangment = () => {
  const { currentLang } = useLanguageStore();

  const [showAddEditItem, setShowAddEditItem] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState({
    value: "all",
    label: localization.all || "All",
  });
  const [filteredItems, setFilteredItems] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editingItem, setEditingItem] = useState(emptyPromptCat);
  const [itemName, setItemName] = useState("");
  const [selectedModule, setSelectedModule] = useState({
    value: "all",
    label: localization.all || "All",
  });
  const [errors, setErrors] = useState({});
  const { promptCategories, setPromptCategories } = usePromptCategoriesStore();
  const { modules } = useModulesStore();

  useEffect(() => {
    filterPromptCategories();
  }, [searchTerm, selectedType, promptCategories, selectedModule]);

  const validateForm = (form) => {
    const e = {};

    const hasValidTitle =
      form.title &&
      Object.values(form.title).some((t) => (t || "").trim() !== "");

    if (!hasValidTitle) {
      e.title =
        localization.title_is_required ||
        "At least one title in any language is required";
    }

    const hasValidDescription =
      form.description &&
      Object.values(form.description).some((t) => (t || "").trim() !== "");

    if (!hasValidDescription) {
      e.description =
        localization.description_is_required ||
        "At least one description in any language is required";
    }

    if (!form.type || !form.type.trim()) {
      e.type = localization.category_type_required || "Select a category type";
    }

    // Template required
    if (!form.module_id || form.module_id === "") {
      e.module_id = localization.module_required || "Select a module";
    }
    return e;
  };

  const handleSubmitItem = async (data) => {
    const validationErrors = validateForm(data);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    try {
      setLoading(true);
      if (editingItem?.id) {
        const res = await SpecificAPI.updatePromptCategory({
          ...data,
          _id: editingItem.id,
        });
        setPromptCategories((prev) =>
          prev.map((ev) => (ev.id === editingItem.id ? res.data : ev)),
        );
      } else {
        const created = await SpecificAPI.createPromptCategory(data);
        setPromptCategories((prev) => [...prev, created.data]);
      }
      setShowAddEditItem(false);
      setEditingItem(emptyPromptCat);

      toast.success(
        localization.prompt_category_toast_saved ||
          "Prompt category saved successfully!",
      );
    } catch (err) {
      console.error("Error saving item:", err);
      toast.error(
        err?.response?.data?.error ||
          localization.prompt_category_toast_failed ||
          "Failed to save prompt category!",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setEditingItem((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleFormChange = (name, value) => {
    setEditingItem((prev) => {
      if (!prev) return { ...emptyPromptCat, [name]: value };
      return { ...prev, [name]: value };
    });

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const filterPromptCategories = () => {
    let filtered = promptCategories;
    if (searchTerm) {
      filtered = filtered.filter((cls) =>
        cls.title?.[currentLang]
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()),
      );
    }

    if (selectedType.value !== "all") {
      filtered = filtered.filter(
        (cls) => cls.type?.toLowerCase() === selectedType.value.toLowerCase(),
      );
    }

    if (selectedModule.value !== "all") {
      filtered = filtered.filter(
        (cls) => cls.module_id === selectedModule.value,
      );
    }

    setFilteredItems(filtered);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowAddEditItem(true);
    setErrors({});
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setItemName(item.title?.[currentLang] || "");
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      await SpecificAPI.deletePromptCategory(itemToDelete.id);

      setPromptCategories((prev) =>
        prev.filter((ev) => ev.id !== itemToDelete.id),
      );

      toast.success("Prompt category deleted successfully");
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete prompt category. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddEditItem(false);
    setEditingItem(emptyPromptCat);
    setErrors({});
  };

  const filteredModuleOptions = [
    { value: "all", label: localization.all || "All" },
    ...modules
      .filter(
        (m) => selectedType.value === "all" || m.type === selectedType.value,
      )
      .map((m) => ({ value: m.id, label: m.name })),
  ];

  return (
    <div className="d-flex flex-column mb-5 w-100 p-8 main-page">
      <div
        className="d-flex flex-column align-items-start mb-5 w-100"
        style={{ paddingRight: "2rem" }}
      >
        <ManagementHeader
          title={localization.all_prompt_categories || "All Prompt Categories"}
          subtitle={
            localization.manage_categories || "Manage prompt categories"
          }
          // icon={MdOutlineCategory}
          buttonText={localization.add_prompt_category || "Add prompt category"}
          onButtonClick={() => {
            setShowAddEditItem(true);
            setEditingItem(emptyPromptCat);
            setErrors({});
          }}
          showBack={true}
          backTo="/schools"
          backLabel="Back to organization"
        />

        {/* Filters Row */}
        <div className="d-flex align-items-center gap-3 w-100 mt-4">
          {/* Search Input with Icon */}
          <InputGroup className="search-field flex-grow-1">
            <InputGroup.Text id="search-addon" className="customSearchAddon">
              <FaSearch className="icon-btn" />
            </InputGroup.Text>
            <Form.Control
              placeholder={
                localization.search_by_prompt_name || "Search by prompt name..."
              }
              aria-label="Search"
              aria-describedby="search-addon"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="customSearchInput"
            />
          </InputGroup>

          {/* Grade Select */}
          <div style={{ flex: "0 0 250px" }}>
            <CustomSelect
              options={ALL_TYPES_OPTIONS}
              value={selectedType}
              onChange={(opt) => setSelectedType(opt)}
              placeholder={localization.select_type || "Select a type"}
            />
          </div>

          <div style={{ flex: "0 0 250px" }}>
            <CustomSelect
              options={filteredModuleOptions}
              value={selectedModule}
              onChange={(opt) => setSelectedModule(opt)}
              placeholder={localization.filter_by_module || "Filter by module"}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <ClipLoader size={40} color="var(--primary-icon)" />
        </div>
      ) : (
        <div className="row g-4 w-100">
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <div key={item.id} className="col-6 col-md-6 col-lg-4 d-flex">
                <PromptMangmentCard
                  item={item}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  currentLang={currentLang}
                  isCategory={true}
                />
              </div>
            ))
          ) : (
            <p className="text-center text-muted">
              {localization.no_prompt_categories || "No prompt categories."}
            </p>
          )}
        </div>
      )}

      <AddEditPromptCategoryModal
        show={showAddEditItem}
        title={
          editingItem?.id
            ? localization.edit_prompt_category || "Edit Prompt category"
            : localization.add_prompt_category_modal || "Add Prompt category"
        }
        formData={editingItem || emptyPromptCat}
        errors={errors}
        onChange={handleChange}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmitItem(editingItem);
        }}
        onClose={handleCloseModal}
        loading={loading}
        modules={modules}
        onFormChange={handleFormChange}
      />

      <DeleteModal
        show={showDeleteModal}
        itemName={itemName}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={loading}
        setLoading={setLoading}
      />
    </div>
  );
};

export default PromptCatMangment;
